"use client";
import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const VIDEOS = [
  { id: 1, src: "/videos/Sana.mp4", name: "Sana Batool", desc: "4 quick snapshots of progress & confidence after our 8-week tracks.", poster: "/images/bg-image.jpg" },
  { id: 2, src: "/videos/Testimony-Q.mp4", name: "Qandeel Zahra", desc: "4 quick snapshots of progress & confidence after our 8-week tracks.", poster: "/images/bg-image.jpg" },
  { id: 3, src: "/videos/Video3.mp4", name: "Ali",  poster: "/images/bg-image.jpg" },
  { id: 4, src: "/videos/Video4.mp4", name: "Abbas Ali",  poster: "/images/bg-image.jpg" },
  { id: 5, src: "/videos/Video5.mp4", name: "Baseer",  poster: "/images/bg-image.jpg" },
 
];

export default function Testimonial() {
 const [index, setIndex] = useState(1);
  const trackRef = useRef(null);
  const containerRef = useRef(null);
  const slideRefs = useRef([]);
  const videoRefs = useRef([]);
  const [translate, setTranslate] = useState(0);
  const prev = () => setIndex((i) => (i === 0 ? VIDEOS.length - 1 : i - 1));
  const next = () => setIndex((i) => (i === VIDEOS.length - 1 ? 0 : i + 1));

  // Play active video only
  useEffect(() => {
    videoRefs.current.forEach((vid, i) => {
      if (!vid) return;
      try {
        if (i === index) {
          if (vid.paused) vid.currentTime = 0;
          const p = vid.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        } else {
          vid.pause();
        }
      } catch (_) {}
    });
  }, [index]);

  // Center active slide
  useEffect(() => {
    const compute = () => {
      const container = containerRef.current;
      const track = trackRef.current;
      const slide = slideRefs.current[index] || slideRefs.current[0];
      if (!container || !track || !slide) return;
      const containerW = container.clientWidth;
      const slideW = slide.clientWidth;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || "0") || 0;
      const offsetToCenter = (containerW - slideW) / 2;
      const x = offsetToCenter - index * (slideW + gap);
      setTranslate(x);
    };
    compute();
    const onResize = () => compute();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [index]);

  return (
    <section
      id="testimonial"
      className="relative pb-16 pt-24 rounded-t-[100px] bg-[#1f2643]"
    >
      {/* Glow background */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 left-[18%] w-[480px] h-[480px] opacity-25 bg-[radial-gradient(circle_at_center,rgba(255,122,24,0.22),transparent_70%)] blur-2xl" />
        <div className="absolute bottom-[-30%] right-[12%] w-[420px] h-[420px] opacity-20 bg-[radial-gradient(circle_at_center,rgba(255,122,24,0.18),transparent_70%)] blur-2xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 md:px-10 flex flex-col items-center text-center">
      <div className="text-center max-w-3xl  mx-auto ">
          <h2 className="text-5xl md:text-6xl sm:mx-20 mx-auto font-semibold tracking-tight text-white"> Testimonials </h2>
          <span className="border-b  block border-white p-1"></span>
          <p className="mt-5 text-xl font-[50] md:text-2xl text-white/90 leading-relaxed">{VIDEOS[index].name}</p>

        </div>

        {/* Carousel */}
        <div className=" w-full max-w-5xl relative">
          <div ref={containerRef} className="overflow-hidden">
            <div
              ref={trackRef}
              className="flex items-stretch sm:gap-2 will-change-transform transition-transform duration-700 ease-[cubic-bezier(.22,1,.36,1)] px-2"
              style={{ transform: `translateX(${translate}px)` }}
            >
              {VIDEOS.map((v, i) => {
                const active = i === index;
                return (
                  <div
                    key={i}
                    ref={(el) => (slideRefs.current[i] = el)}
                    className="basis-[70%] sm:basis-[68%] md:basis-[54%] lg:basis-[46%] shrink-0 px-1"
                  >
                    <div
                      className={`relative aspect-[4/6] sm:aspect-[2/3] rounded-[28px] overflow-hidden  transform-gpu transition-all duration-700 ease-[cubic-bezier(.22,1,.36,1)] ${
                        active
                          ? "z-20   scale-90"
                          : "z-10  scale-80"
                      }`}
                    >
                      <video
                        className="w-full h-full object-cover rounded-[28px]"
                        src={v.src}
                        autoPlay
                        muted
                        loop
                        controls
                        playsInline
                        preload="metadata"
                        ref={(el) => (videoRefs.current[i] = el)}
                      />
                      {/* Dark overlay for non-active */}
                      {!active && (
                        <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Navigation buttons inside carousel */}
            <div className="absolute inset-y-0 left-7 right-3 flex items-center justify-between px-3 pointer-events-none">
              <button
                onClick={prev}
                className="pointer-events-auto size-7 cursor-pointer text-xl sm:text-2xl flex items-center justify-center rounded-full  text-brand sm:size-10 bg-white border border-white/20 hover:bg-white/70 transition"
              >
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
              </button>
              <button
                onClick={next}
                className="pointer-events-auto size-7 cursor-pointer text-xl sm:text-2xl flex items-center justify-center rounded-full bg-white text-brand sm:size-10 border border-white/20 hover:bg-white/70 transition"
              >
                <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-black"/>
              </button>
            </div>
          </div>

          {/* Dots */}
          <div className="mt-6 flex items-center justify-center gap-2">
            {VIDEOS.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`h-2.5 rounded-full transition-all ${
                  index === i
                    ? "w-6 bg-white"
                    : "w-2.5 bg-white/30 hover:bg-brand/70"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
